/**
 * SSO Service
 * Handles Single Sign-On integration with SAML and OAuth providers
 */

import { apiClient } from './api-client';
import { auditService } from './audit-service';
import { EnhancedUser } from './rbac-types';

// ============================================================================
// SSO TYPES
// ============================================================================

export type SSOProvider = 'google' | 'microsoft' | 'okta' | 'auth0' | 'saml';

export interface SSOConfig {
  provider: SSOProvider;
  enabled: boolean;
  client_id?: string;
  client_secret?: string;
  issuer_url?: string;
  redirect_uri: string;
  scopes?: string[];

  // SAML-specific
  entity_id?: string;
  sso_url?: string;
  x509_cert?: string;
  metadata_url?: string;

  // Provider-specific settings
  domain_hint?: string; // Microsoft
  hd?: string; // Google Workspace domain

  // Auto-provisioning settings
  auto_provision: boolean;
  default_role: string;
  attribute_mapping: {
    email: string;
    first_name: string;
    last_name: string;
    groups?: string;
  };
}

export interface SSOAuthRequest {
  provider: SSOProvider;
  redirect_url?: string;
  state?: string;
  org_id?: string;
}

export interface SSOAuthResponse {
  access_token: string;
  user: EnhancedUser;
  is_new_user: boolean;
  provider_data: Record<string, any>;
}

export interface SSOCallbackData {
  code?: string;
  state?: string;
  error?: string;
  error_description?: string;
  saml_response?: string;
}

export interface ProviderUserInfo {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  avatar_url?: string;
  groups?: string[];
  raw_data: Record<string, any>;
}

// ============================================================================
// SSO SERVICE
// ============================================================================

export class SSOService {
  private static instance: SSOService;
  private configs: Map<string, SSOConfig> = new Map();

  private constructor() {}

  static getInstance(): SSOService {
    if (!SSOService.instance) {
      SSOService.instance = new SSOService();
    }
    return SSOService.instance;
  }

  /**
   * Get available SSO providers for an organization
   */
  async getAvailableProviders(orgId?: string): Promise<SSOConfig[]> {
    try {
      const response = await apiClient.get<SSOConfig[]>(`/api/v1/sso/providers`, {
        params: { org_id: orgId },
        cacheKey: `sso-providers-${orgId || 'global'}`,
        cacheTTL: 5 * 60 * 1000 // 5 minutes
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch SSO providers');
      }

      // Update local cache
      response.data?.forEach(config => {
        this.configs.set(`${config.provider}-${orgId || 'global'}`, config);
      });

      return response.data || [];
    } catch (error) {
      console.warn('SSO providers API not available, using mock data:', error);
      return this.getMockProviders();
    }
  }

  /**
   * Initialize OAuth flow
   */
  async initiateOAuth(request: SSOAuthRequest): Promise<string> {
    const config = this.configs.get(`${request.provider}-${request.org_id || 'global'}`);
    if (!config) {
      throw new Error(`SSO provider ${request.provider} not configured`);
    }

    const authUrl = this.buildAuthUrl(config, request);

    // Log SSO attempt
    await auditService.logAction({
      action: 'sso:oauth_initiated',
      resource_type: 'authentication',
      details: {
        provider: request.provider,
        org_id: request.org_id,
        redirect_url: request.redirect_url
      }
    });

    return authUrl;
  }

  /**
   * Handle OAuth callback
   */
  async handleOAuthCallback(
    provider: SSOProvider,
    callbackData: SSOCallbackData,
    orgId?: string
  ): Promise<SSOAuthResponse> {
    if (callbackData.error) {
      await auditService.logAction({
        action: 'sso:oauth_failed',
        resource_type: 'authentication',
        details: {
          provider,
          error: callbackData.error,
          error_description: callbackData.error_description
        },
        success: false,
        error_message: callbackData.error_description
      });

      throw new Error(`OAuth error: ${callbackData.error_description || callbackData.error}`);
    }

    try {
      const response = await apiClient.post<SSOAuthResponse>('/api/v1/sso/oauth/callback', {
        provider,
        code: callbackData.code,
        state: callbackData.state,
        org_id: orgId
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'OAuth callback failed');
      }

      // Log successful SSO login
      await auditService.logAction({
        action: 'sso:oauth_success',
        resource_type: 'authentication',
        details: {
          provider,
          user_id: response.data!.user.user_id,
          is_new_user: response.data!.is_new_user
        }
      });

      return response.data!;
    } catch (error) {
      await auditService.logAction({
        action: 'sso:oauth_failed',
        resource_type: 'authentication',
        details: {
          provider,
          error: error instanceof Error ? error.message : 'Unknown error'
        },
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error'
      });

      throw error;
    }
  }

  /**
   * Handle SAML authentication
   */
  async handleSAMLCallback(
    callbackData: SSOCallbackData,
    orgId?: string
  ): Promise<SSOAuthResponse> {
    try {
      const response = await apiClient.post<SSOAuthResponse>('/api/v1/sso/saml/callback', {
        saml_response: callbackData.saml_response,
        org_id: orgId
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'SAML authentication failed');
      }

      // Log successful SAML login
      await auditService.logAction({
        action: 'sso:saml_success',
        resource_type: 'authentication',
        details: {
          user_id: response.data!.user.user_id,
          is_new_user: response.data!.is_new_user
        }
      });

      return response.data!;
    } catch (error) {
      await auditService.logAction({
        action: 'sso:saml_failed',
        resource_type: 'authentication',
        details: {
          error: error instanceof Error ? error.message : 'Unknown error'
        },
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error'
      });

      throw error;
    }
  }

  /**
   * Get SAML metadata
   */
  async getSAMLMetadata(orgId?: string): Promise<string> {
    try {
      const response = await apiClient.get<{ metadata: string }>('/api/v1/sso/saml/metadata', {
        params: { org_id: orgId }
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to get SAML metadata');
      }

      return response.data!.metadata;
    } catch (error) {
      console.warn('SAML metadata API not available:', error);
      return this.getMockSAMLMetadata();
    }
  }

  /**
   * Configure SSO provider
   */
  async configureSSOProvider(config: SSOConfig, orgId?: string): Promise<void> {
    try {
      const response = await apiClient.post('/api/v1/sso/configure', {
        ...config,
        org_id: orgId
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to configure SSO provider');
      }

      // Update local cache
      this.configs.set(`${config.provider}-${orgId || 'global'}`, config);

      // Log configuration change
      await auditService.logAction({
        action: 'sso:provider_configured',
        resource_type: 'configuration',
        details: {
          provider: config.provider,
          enabled: config.enabled,
          auto_provision: config.auto_provision
        }
      });
    } catch (error) {
      await auditService.logAction({
        action: 'sso:config_failed',
        resource_type: 'configuration',
        details: {
          provider: config.provider,
          error: error instanceof Error ? error.message : 'Unknown error'
        },
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error'
      });

      throw error;
    }
  }

  /**
   * Test SSO configuration
   */
  async testSSOConfiguration(provider: SSOProvider, orgId?: string): Promise<boolean> {
    try {
      const response = await apiClient.post<{ valid: boolean; details?: string }>('/api/v1/sso/test', {
        provider,
        org_id: orgId
      });

      return response.success && response.data!.valid;
    } catch (error) {
      console.error('Failed to test SSO configuration:', error);
      return false;
    }
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  private buildAuthUrl(config: SSOConfig, request: SSOAuthRequest): string {
    const params = new URLSearchParams({
      client_id: config.client_id || '',
      redirect_uri: config.redirect_uri,
      response_type: 'code',
      scope: config.scopes?.join(' ') || 'openid email profile',
      state: request.state || crypto.randomUUID()
    });

    if (config.provider === 'microsoft' && config.domain_hint) {
      params.append('domain_hint', config.domain_hint);
    }

    if (config.provider === 'google' && config.hd) {
      params.append('hd', config.hd);
    }

    const baseUrls = {
      google: 'https://accounts.google.com/o/oauth2/v2/auth',
      microsoft: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
      okta: `${config.issuer_url}/v1/authorize`,
      auth0: `${config.issuer_url}/authorize`
    };

    const baseUrl = baseUrls[config.provider as keyof typeof baseUrls];
    return `${baseUrl}?${params.toString()}`;
  }

  private getMockProviders(): SSOConfig[] {
    return [
      {
        provider: 'google',
        enabled: true,
        client_id: 'mock-google-client-id',
        redirect_uri: `${window.location.origin}/auth/sso/callback/google`,
        scopes: ['openid', 'email', 'profile'],
        auto_provision: true,
        default_role: 'viewer',
        attribute_mapping: {
          email: 'email',
          first_name: 'given_name',
          last_name: 'family_name'
        }
      },
      {
        provider: 'microsoft',
        enabled: false,
        client_id: 'mock-microsoft-client-id',
        redirect_uri: `${window.location.origin}/auth/sso/callback/microsoft`,
        scopes: ['openid', 'email', 'profile'],
        auto_provision: true,
        default_role: 'viewer',
        attribute_mapping: {
          email: 'email',
          first_name: 'given_name',
          last_name: 'family_name'
        }
      },
      {
        provider: 'saml',
        enabled: false,
        entity_id: 'briefcase-ai-telemetry',
        sso_url: 'https://mock-idp.example.com/saml/sso',
        redirect_uri: `${window.location.origin}/auth/sso/callback/saml`,
        auto_provision: true,
        default_role: 'viewer',
        attribute_mapping: {
          email: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
          first_name: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname',
          last_name: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname',
          groups: 'http://schemas.microsoft.com/ws/2008/06/identity/claims/groups'
        }
      }
    ];
  }

  private getMockSAMLMetadata(): string {
    return `<?xml version="1.0" encoding="UTF-8"?>
<md:EntityDescriptor xmlns:md="urn:oasis:names:tc:SAML:2.0:metadata" entityID="briefcase-ai-telemetry">
  <md:SPSSODescriptor AuthnRequestsSigned="false" WantAssertionsSigned="true" protocolSupportEnumeration="urn:oasis:names:tc:SAML:2.0:protocol">
    <md:NameIDFormat>urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress</md:NameIDFormat>
    <md:AssertionConsumerService Binding="urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST" Location="${window.location.origin}/auth/sso/callback/saml" index="1"/>
  </md:SPSSODescriptor>
</md:EntityDescriptor>`;
  }
}

// Singleton instance
export const ssoService = SSOService.getInstance();

export default ssoService;